import React, { useState } from "react";
import { Accordion, AccordionBox, AccordionButtonTitle, ModelIcon, ModelText, ModelName } from "../../AccordionUI";
import { CalendarIcon, DetailIcon, FactorIcon, NewsIcon, OpenArrowDownIcon, TwitterIcon, ClockIcon } from "../../../Icons/Icons";
import { BlankSpacer, Line } from "../../../StyledComponents/AppStyledComponents";
import { formatDateTime } from "../../../../Controllers/utils";
import StockInfo from "../../StockInfo";
import { CFDs } from "../../../constants";
import MoveClassificationView from "../../StockCard/components/MoveClassifications";
import MacroSummariesFeed from "../../../Views/FeedViewer/MacroSummariesFeed";



const categoryOrder = [
    'news_social',
    'flow_volume',
    'events',
    'cross_asset',
  ];

const ModelSummaryAccordion = ({ modelData, assetDescription, etfData, showInfo, chartSelectedDate }) => {
    const [categoryOpen, setCategoryOpen] = useState({
        news_social: true,
        flow_volume: true,
        events: true,
        cross_asset: true,
        sector_overview: true,
        macro_summaries: true,
        asset_description: true,
    });

    const handleToggleAccordion = (category) => {
        setCategoryOpen({
            ...categoryOpen,
            [category]: !categoryOpen[category],
        });

    };

    let utcPlusTen = new Date(etfData?.dtm_summary?.time_utc);
    utcPlusTen.setMinutes(utcPlusTen.getMinutes() + 10);  

    const filteredAndSorted = modelData?.filter(model => model.summary !== null)
        .sort((a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category))

    return (
        <>
            {modelData ? (
                <Accordion>
                    {etfData && (
                        <>
                            <AccordionButtonTitle 
                                onClick={() => handleToggleAccordion('sector_overview')} 
                                isOpen={categoryOpen.sector_overview}
                                backgroundColor='BETA'  
                            >
                                Sector Overview
                                <OpenArrowDownIcon isOpen={categoryOpen.sector_overview}/>
                            </AccordionButtonTitle>
                            <AccordionBox isOpen={categoryOpen.sector_overview}>
                                <div style={{ width: '100%' }}>
                                    <ModelText padding='5px 20px' fontSize='16px' color="white">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                            <div style={{ display: 'flex', flex: '1 1 auto', alignItems: 'center', gap: '8px' }}>
                                                {!CFDs.includes(etfData?.type) &&
                                                    <ClockIcon style={{ margin: 0, display: 'flex', alignItems: 'center' }} />
                                                }
                                                <h3 style={{fontSize: '16px', fontWeight: '600', margin: 0, lineHeight: '1.2'}}>
                                                    {etfData?.name.toUpperCase()}
                                                </h3>
                                            </div>
                                            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                                                <StockInfo data={etfData}  expandedView={true}/>
                                            </div>
                                        </div>

                                        <MoveClassificationView
                                            macro={etfData?.move_classification?.dtm_macro}
                                            sector={etfData?.move_classification?.dtm_sector}
                                            micro={etfData?.move_classification?.dtm_micro}
                                            percentile={etfData?.date_return_oc_percentile}
                                            type={etfData?.type}
                                        />
                                        
                                        {etfData?.dtm_summary && (
                                            <p style={{ marginTop: '15px' }}>
                                                {etfData?.dtm_summary?.dtm_summary?.split(/\n\n|\n/).map((line, idx) => (
                                                    <React.Fragment key={idx}>
                                                        {line}
                                                        <br />
                                                    </React.Fragment>
                                                ))}
                                            </p>
                                        )}
                                        
                                        {etfData?.dtm_summary?.time_utc && (
                                            <p style={{ marginTop: '10px' }}>
                                                Published: {formatDateTime(etfData?.dtm_summary?.time_utc)}
                                            </p>
                                        )}
                                    </ModelText>
                                </div>
                            </AccordionBox>
                        </>
                    )}
                    
                    {showInfo && chartSelectedDate && (
                        <>
                            <AccordionButtonTitle 
                                onClick={() => handleToggleAccordion('macro_summaries')} 
                                isOpen={categoryOpen.macro_summaries}
                                backgroundColor='BETA'
                            >
                                Macro Overview
                                <OpenArrowDownIcon isOpen={categoryOpen.macro_summaries}/>
                            </AccordionButtonTitle>
                            <AccordionBox isOpen={categoryOpen.macro_summaries}>
                                <div style={{ width: '100%' }}>
                                    <MacroSummariesFeed 
                                        chartSelectedDate={chartSelectedDate} 
                                        chartInfo={true}
                                        defaultExpanded={true}
                                    />
                                </div>
                            </AccordionBox>
                        </>
                    )}
                    
                    {filteredAndSorted
                        .map((model, index) => {
                            const key = model.symbol + model.mdl_name;
                            let accordionTitle;
                            let modelTagIcon;
                            const { category } = model;

                            switch (category) {
                                case 'flow_volume':
                                    accordionTitle = 'Flow & Volume';
                                    modelTagIcon = <DetailIcon />;
                                    break;
                                case 'cross_asset':
                                    accordionTitle = 'Cross-Asset Drivers';
                                    modelTagIcon = <FactorIcon />;
                                    break;
                                case 'news_social':
                                    accordionTitle = 'News & Social Media';
                                    modelTagIcon = <NewsIcon />;
                                    if (model.short_tag === 'social') {
                                        modelTagIcon = <TwitterIcon />;
                                    }
                                    break;
                                case 'events':
                                    accordionTitle = 'Events';
                                    modelTagIcon = <CalendarIcon />;
                                    break;
                                default:
                                    accordionTitle = model.tag;
                            }

                            if (key !== 'NaN' && model.summary != null) {
                                if (category === filteredAndSorted[index - 1]?.category) {
                                    return (
                                        <AccordionBox isOpen={categoryOpen[category]}>
                                            <ModelIcon>{modelTagIcon}</ModelIcon>
                                            <ModelName>{model.name}</ModelName>
                                            <div>
                                                <ModelText>
                                                    {model.summary.split('\n\n').map(summary => (
                                                        <>
                                                            <p>{summary}</p>
                                                            <br />
                                                        </>
                                                    ))}
                                                    <p>Published: {formatDateTime(model.time_utc)}</p>
                                                </ModelText>
                                            </div>
                                        </AccordionBox>
                                    );
                                }
                                return (
                                    <>
                                        <AccordionButtonTitle onClick={() => handleToggleAccordion(category)} isOpen={categoryOpen[category]}>
                                            {accordionTitle}
                                            <OpenArrowDownIcon isOpen={categoryOpen[category]}/>
                                        </AccordionButtonTitle>
                                        <AccordionBox isOpen={categoryOpen[category]}>
                                            <ModelIcon>{modelTagIcon}</ModelIcon>
                                            <ModelName>{model.name}</ModelName>
                                            <div>
                                                <ModelText>
                                                    {model.summary.split('\n\n').map(summary => (
                                                        <>
                                                            <p>{summary}</p>
                                                            <br />
                                                        </>
                                                    ))}
                                                    <p>Published: {formatDateTime(model.time_utc)}</p>
                                                </ModelText>
                                            </div>
                                        </AccordionBox>
                                       
                                    </>
                                );
                            }
                            return null;
                        })}
                        
                        {assetDescription && (
                            <>
                                <AccordionButtonTitle 
                                    onClick={() => handleToggleAccordion('asset_description')} 
                                    isOpen={categoryOpen.asset_description}
                                >
                                    Asset Description
                                    <OpenArrowDownIcon isOpen={categoryOpen.asset_description}/>
                                </AccordionButtonTitle>
                                <AccordionBox isOpen={categoryOpen.asset_description}>
                                    <ModelText>
                                        <p>{assetDescription}</p>
                                    </ModelText>
                                </AccordionBox>
                            </>
                        )}
                        <Line />
                        <BlankSpacer />
                </Accordion>
            ) : null}
        </>
    );
};

export default ModelSummaryAccordion;